import type { MatchField } from '@threadheads/shared';

/**
 * Render a rule's `value` / `display_label` pair for the UI. If the label
 * exists and differs from the raw value, we prefer the label (e.g. product
 * title) and use the raw value only as muted secondary detail. If only the
 * raw value is present and it's a Shopify GID, we strip the verbose prefix
 * so it reads as a numeric ID instead of `gid://shopify/Product/12345`.
 */
export function formatRuleLabel(args: {
  matchField: MatchField;
  value: string;
  displayLabel: string | null | undefined;
}): { primary: string; secondary?: string } {
  const { matchField, value, displayLabel } = args;

  if (displayLabel && displayLabel.trim() && displayLabel !== value) {
    return { primary: displayLabel, secondary: formatValueSecondary(matchField, value) };
  }

  // No display label or same as value — use a friendly rendering of the value.
  if (isGid(value)) {
    const numeric = extractGidNumeric(value);
    if (numeric) return { primary: `#${numeric}` };
  }
  return { primary: value };
}

function formatValueSecondary(matchField: MatchField, value: string): string | undefined {
  if (matchField === 'shopify_product_id' || matchField === 'shopify_variant_id') {
    const numeric = extractGidNumeric(value);
    return numeric ? `#${numeric}` : undefined;
  }
  return undefined;
}

function isGid(v: string): boolean {
  return v.startsWith('gid://shopify/');
}

function extractGidNumeric(v: string): string | null {
  const m = v.match(/\/(\d+)(?:[?#].*)?$/);
  return m ? m[1] ?? null : null;
}
